import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Loader from 'components/Loader';
import PropertyInfo from 'app/components/Map/PropertyInfo';
import { loadContact } from 'data/contacts';
import { getProperty, lookupProperty } from 'data/property/actions';
import { toPropertyRecord } from 'data/property/reducer';
import { fetchInvestorProfile, fetchInvestorProperties } from 'data/investors/investorProfileService';

import InvestorLeadCard from './index';
import InvestorPropertyCard, { formatAddress } from './InvestorPropertyCard';

// Decides what the dialer's right-hand card shows for the current lead
// (ATAI-520): for an Investor Outreach contact, one of the investor's
// properties in the same compact property card a property lead gets;
// otherwise `children` (the property preview card / its placeholder) unchanged.
//
// Whether a lead is an investor can't be read off `propertyInfo`: most
// investor leads have none, but some come back with an empty property. The
// contact's own `batchdataInvestorId` is the reliable signal, so load the
// contact the same way Contacts/InvestorProfileRoute does, then the investor's
// stored profile (the search's investor object).
//
// The property shown is the first of the investor's properties, in the order
// the investor's search card lists them under "Owned Properties"
// (Results/OwnedProperties: currently-owned first), that PropStream loads WITH
// an address. The investor service's `propertyId` doesn't always resolve in
// PropStream ("Property not found"), and some resolve to a property with a
// null address, so the candidates are tried in turn. It's rendered exactly
// like a property lead's card (components/Map/PropertyInfo, compact). When
// none loads, the first candidate with an address is shown from the
// investor's own record (InvestorPropertyCard); with no address anywhere, the
// investor summary (InvestorLeadCard).
//
// Properties are fetched with lookupProperty, which neither writes the store
// (the dialer's own card reads GET_PROPERTY's slot for the current lead's
// property) nor pops the global error alert. All lookups are cached, so
// stepping back and forth between leads doesn't refetch.
const investorIdByContact = new Map(); // contactId -> batchdataInvestorId | null
const profileByInvestor = new Map(); // batchdataInvestorId -> profile | null
const previewByInvestor = new Map(); // batchdataInvestorId -> { property, record }
const propertyById = new Map(); // propertyId -> Promise<Immutable property | null>

// How many candidates to try loading before settling for the investor's record.
const MAX_LOOKUPS = 10;

// Currently-owned first, as normalizeSearchResponse orders them for the card.
const ownedFirst = (properties) => {
  const list = (properties || []).filter(Boolean);
  return list.filter((p) => p.currentOwner).concat(list.filter((p) => !p.currentOwner));
};

// The stored profile's properties (the search card's own list), then the
// investor's owned-properties rows (the Profile's property table source) for a
// profile saved without them, current ownership before sold.
const getCandidates = (profile, investorId) => {
  const fromProfile = ownedFirst(profile && profile.properties);
  if (fromProfile.length) return Promise.resolve(fromProfile);

  return fetchInvestorProperties(investorId, 'current')
    .then(({ data }) => {
      const rows = ((data && data.properties) || []).filter(Boolean);
      return rows.filter((p) => !p.saleInfo).concat(rows.filter((p) => p.saleInfo));
    })
    .catch(() => []);
};

// PropStream can return a property with a null address, which PropertyInfo
// renders as an empty/"null" card, so only one with a street address counts —
// by PropertyInfo's own street checks, which also hide placeholders like
// "<No Street Address>" and "undefined".
const isValidStreet = (street) =>
  typeof street === 'string' &&
  street.trim() !== '' &&
  !street.includes('0000') &&
  !street.includes('<No Street Address>') &&
  street.toLowerCase() !== 'undefined';

const hasAddress = (property) => Boolean(property && isValidStreet(property.getIn(['address', 'streetAddress'])));

const loadProperty = (dispatch, propertyId) => {
  if (!propertyById.has(propertyId)) {
    propertyById.set(
      propertyId,
      new Promise((resolve) => {
        const result = dispatch(
          lookupProperty(
            propertyId,
            'M',
            ({ response } = {}) => {
              const loaded = response && response.id ? toPropertyRecord(response) : null;
              resolve(hasAddress(loaded) ? loaded : null);
            },
            () => resolve(null)
          )
        );
        if (result && result.catch) result.catch(() => resolve(null));
      })
    );
  }
  return propertyById.get(propertyId);
};

// First candidate PropStream loads with an address, else the first candidate
// whose own record has one.
const resolvePreview = async (dispatch, candidates) => {
  const ids = [...new Set(candidates.map((p) => p.propertyId).filter(Boolean))].slice(0, MAX_LOOKUPS);
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    const property = await loadProperty(dispatch, id);
    if (property) return { property, record: null };
  }
  return { property: null, record: candidates.find((p) => formatAddress(p)) || null };
};

const centered = { display: 'flex', alignItems: 'center', justifyContent: 'center' };

// `history` comes from the dialer (withRouter) rather than useHistory(): the
// router hook resolves React through a separate chunk here and throws
// "Cannot read properties of null (reading 'useContext')".
const CurrentLeadPreview = ({ lead, history, children }) => {
  const dispatch = useDispatch();
  const contactId = lead && lead.contactInfo ? lead.contactInfo.id : null;

  const [investorId, setInvestorId] = useState(() =>
    contactId != null && investorIdByContact.has(contactId) ? investorIdByContact.get(contactId) : undefined
  );
  const [profile, setProfile] = useState(() =>
    investorId && profileByInvestor.has(investorId) ? profileByInvestor.get(investorId) : undefined
  );
  // undefined while resolving, then { property, record } (either may be null)
  const [preview, setPreview] = useState(() => (investorId ? previewByInvestor.get(investorId) : undefined));

  // contact -> investor id -> stored profile
  useEffect(() => {
    if (contactId == null) return undefined;
    let cancelled = false;

    const resolveProfile = (id) => {
      if (!id) return;
      if (profileByInvestor.has(id)) {
        setProfile(profileByInvestor.get(id));
        return;
      }
      setProfile(undefined);
      fetchInvestorProfile(id)
        .then(({ data }) => data || null)
        .catch(() => null) // never materialized into a snapshot (404) — card falls back
        .then((data) => {
          profileByInvestor.set(id, data);
          if (!cancelled) setProfile(data);
        });
    };

    if (investorIdByContact.has(contactId)) {
      const id = investorIdByContact.get(contactId);
      setInvestorId(id);
      resolveProfile(id);
      return () => {
        cancelled = true;
      };
    }

    setInvestorId(undefined);
    setProfile(undefined);
    const settle = (id) => {
      investorIdByContact.set(contactId, id || null);
      if (cancelled) return;
      setInvestorId(id || null);
      resolveProfile(id);
    };

    // loadContact's callback gets `{ response }`, the body wrapping the contact
    // again as `{ response: <contact> }` (same double unwrap as InvestorProfileRoute).
    dispatch(
      loadContact(
        contactId,
        ({ response } = {}) => settle(response?.response?.batchdataInvestorId),
        () => settle(null)
      )
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  // profile -> the property to show
  useEffect(() => {
    if (!investorId || profile === undefined) return undefined;
    let cancelled = false;

    if (previewByInvestor.has(investorId)) {
      setPreview(previewByInvestor.get(investorId));
      return undefined;
    }

    setPreview(undefined);
    getCandidates(profile, investorId)
      .then((candidates) => resolvePreview(dispatch, candidates))
      .catch(() => ({ property: null, record: null }))
      .then((result) => {
        previewByInvestor.set(investorId, result);
        if (!cancelled) setPreview(result);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId, profile]);

  const loader = (
    <div style={centered}>
      <Loader loaderSize="small" />
    </div>
  );

  // Still finding out whether this lead is an investor: hold the slot rather
  // than flash the empty property card first.
  if (contactId != null && investorId === undefined) return loader;
  if (!investorId) return children;

  if (profile === undefined || preview === undefined) return loader;

  if (preview.property) {
    return (
      <PropertyInfo
        info={preview.property}
        property={preview.property}
        history={history}
        loading={false}
        getProperty={(...args) => dispatch(getProperty(...args))}
        saveEnabled={false}
        nearbySearchEnabled={false}
        photoGalleryEnabled={false}
        closeButtonEnabled={false}
        hoverEffectsEnabled={false}
        cardStyle="compact"
        searchType={null}
      />
    );
  }

  // None of the investor's properties loaded in PropStream: the first one with
  // an address, from the investor's own record.
  if (preview.record) {
    return (
      <InvestorPropertyCard
        investorId={investorId}
        contactId={contactId}
        profile={profile || null}
        property={preview.record}
      />
    );
  }

  // No property with an address at all: the investor summary.
  return (
    <InvestorLeadCard
      investorId={investorId}
      contactId={contactId}
      contactInfo={lead.contactInfo}
      profile={profile || null}
      loading={false}
    />
  );
};

export default CurrentLeadPreview;
